import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogoMark } from "../../components/Logo";
import { useAuth } from "../../context/AuthContext";
import { formatApiError } from "../../services/api";
import { ArrowRight, UserPlus, CheckCircle2 } from "lucide-react";

const Register = () => {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({
        name: "",
        company: "",
        email: "",
        password: "",
    });
    const [error, setError] = useState("");
    const [done, setDone] = useState(false);
    const [busy, setBusy] = useState(false);

    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setBusy(true);
        try {
            await register({
                name: form.name.trim(),
                company: form.company.trim(),
                email: form.email.trim().toLowerCase(),
                password: form.password,
            });
            setDone(true);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    if (done) {
        return (
            <div className="min-h-screen ns-cross-pattern flex items-center justify-center p-6">
                <div className="max-w-md w-full bg-white border border-[var(--ns-border)] p-10 text-center">
                    <CheckCircle2
                        size={36}
                        strokeWidth={1.6}
                        className="mx-auto"
                        style={{ color: "var(--ns-ink)" }}
                    />
                    <h2
                        className="font-display text-2xl lg:text-3xl font-bold tracking-tight mt-5"
                        style={{ color: "var(--ns-ink)" }}
                    >
                        Request received
                    </h2>
                    <p
                        className="font-body mt-4 text-[15px] leading-relaxed"
                        style={{ color: "var(--ns-body)" }}
                    >
                        Your account has been submitted for review. An
                        administrator will validate your access shortly. You
                        will be able to sign in once approved.
                    </p>
                    <Link
                        to="/portal/login"
                        className="mt-8 inline-flex items-center gap-2 ns-btn-primary"
                        data-testid="register-success-back"
                    >
                        Back to sign in <ArrowRight size={16} />
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div
            className="min-h-screen grid lg:grid-cols-2 bg-white"
            data-testid="register-page"
        >
            <div className="hidden lg:flex ns-cross-pattern p-12 flex-col justify-between">
                <Link to="/" className="flex items-center gap-3">
                    <LogoMark size={36} />
                    <div className="leading-none">
                        <div className="font-display text-base font-semibold tracking-tight">
                            Nador Sage International
                        </div>
                        <div
                            className="font-body text-[10px] uppercase tracking-[0.22em] mt-1"
                            style={{ color: "var(--ns-muted)" }}
                        >
                            S.r.l.
                        </div>
                    </div>
                </Link>
                <div className="max-w-md">
                    <span className="ns-eyebrow">Request Access</span>
                    <h1
                        className="font-display mt-6 text-4xl xl:text-5xl font-bold leading-[1.05] tracking-tight"
                        style={{ color: "var(--ns-ink)" }}
                    >
                        Apply to access the
                        <br />
                        RFQ Portal.
                    </h1>
                    <p
                        className="font-body mt-6 text-[15px] leading-relaxed"
                        style={{ color: "var(--ns-muted)" }}
                    >
                        Registration is subject to administrator approval. You
                        will be notified once your account is validated.
                    </p>
                </div>
                <div
                    className="font-body text-[11px] uppercase tracking-[0.22em]"
                    style={{ color: "var(--ns-muted)" }}
                >
                    © {new Date().getFullYear()} Nador Sage International S.r.l.
                </div>
            </div>

            <div className="flex items-center justify-center p-6 lg:p-12">
                <form
                    onSubmit={onSubmit}
                    className="w-full max-w-md"
                    data-testid="register-form"
                >
                    <UserPlus
                        size={22}
                        style={{ color: "var(--ns-ink)" }}
                        className="mb-6"
                    />
                    <h2
                        className="font-display text-3xl lg:text-4xl font-bold tracking-tight"
                        style={{ color: "var(--ns-ink)" }}
                    >
                        Request access
                    </h2>
                    <p
                        className="font-body mt-3 text-sm"
                        style={{ color: "var(--ns-muted)" }}
                    >
                        Provide your details — an administrator will approve
                        your account.
                    </p>

                    {error && (
                        <div
                            className="mt-6 px-4 py-3 border bg-[#fff3f1] border-[#e6c8c2] font-body text-sm"
                            style={{ color: "#7a2218" }}
                            data-testid="register-error"
                        >
                            {error}
                        </div>
                    )}

                    <div className="mt-8 space-y-5">
                        <Field
                            id="name"
                            label="Full Name"
                            value={form.name}
                            onChange={onChange}
                            required
                        />
                        <Field
                            id="company"
                            label="Company"
                            value={form.company}
                            onChange={onChange}
                        />
                        <Field
                            id="email"
                            type="email"
                            label="Email"
                            value={form.email}
                            onChange={onChange}
                            required
                            autoComplete="email"
                        />
                        <Field
                            id="password"
                            type="password"
                            label="Password (min. 6 chars)"
                            value={form.password}
                            onChange={onChange}
                            required
                            minLength={6}
                            autoComplete="new-password"
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={busy}
                        className="mt-8 ns-btn-primary w-full justify-center"
                        data-testid="register-submit"
                    >
                        {busy ? "Submitting…" : "Submit request"}
                        <ArrowRight size={16} />
                    </button>

                    <div className="mt-6 text-sm text-center">
                        <Link
                            to="/portal/login"
                            className="font-body"
                            style={{ color: "var(--ns-muted)" }}
                        >
                            Already have access? <span style={{ color: "var(--ns-ink)" }}>Sign in</span>
                        </Link>
                    </div>
                </form>
            </div>
        </div>
    );
};

const Field = ({ id, label, ...props }) => (
    <div>
        <label
            htmlFor={id}
            className="block font-body text-[11px] uppercase tracking-[0.22em] mb-2"
            style={{ color: "var(--ns-muted)" }}
        >
            {label}
        </label>
        <input
            id={id}
            name={id}
            {...props}
            className="w-full bg-white border border-[var(--ns-border)] px-4 py-3 font-body text-base focus:outline-none focus:border-[var(--ns-ink)] transition-colors"
            style={{ color: "var(--ns-ink)" }}
        />
    </div>
);

export default Register;
